import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Steps,
  message,
  Typography,
  Space,
  Alert,
  Progress,
  Modal,
  Form,
  Select,
  List,
  Tag,
  Popconfirm,
  Empty,
  Pagination,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  DeleteOutlined,
  CalendarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import VideoRecorder from "../../../components/features/VideoRecorder";
import {
  testimonialService,
  fileUploadService,
  authService,
} from "../../../services";

const { Title, Text } = Typography;
const { Option } = Select;

const RecordTestimonial = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userTestimonials, setUserTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("record");
  const [form] = Form.useForm();

  // ========== ADDED: Pagination State ==========
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const authData = authService.getCurrentUser();

  useEffect(() => {
    if (location.state?.autoSwitchToTestimonials) {
      setActiveTab("myTestimonials");
    }
  }, [location.state]);

  const steps = [
    {
      title: "Choose Method",
      description: "Record or upload",
    },
    {
      title: "Create Video",
      description: "Record or select video",
    },
    {
      title: "Review & Submit",
      description: "Preview and details",
    },
  ];

  // ========== UPDATED: Fetch with pagination ==========
  const fetchUserTestimonials = async (page = 1, pageSize = 10) => {
    try {
      setTestimonialsLoading(true);
      const response = await testimonialService.getUserTestimonials(
        page,
        pageSize
      );

      if (response.status) {
        setUserTestimonials(response.data.testimonials || []);
        setPagination({
          current: response.data.pagination?.currentPage || page,
          pageSize: pageSize,
          total: response.data.pagination?.totalItems || 0,
        });
      } else {
        message.error("Failed to fetch testimonials");
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      message.error("Error loading testimonials");
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    try {
      const response = await testimonialService.deleteTestimonial(
        testimonialId
      );

      if (response.status) {
        message.success("Testimonial deleted successfully");
        // Reload current page after deletion
        fetchUserTestimonials(pagination.current, pagination.pageSize);
      } else {
        throw new Error(response.message || "Failed to delete testimonial");
      }
    } catch (error) {
      console.error("Delete error:", error);
      message.error("Failed to delete testimonial: " + error.message);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "processing";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // ========== UPDATED: Fetch testimonials on tab change with pagination ==========
  useEffect(() => {
    if (activeTab === "myTestimonials") {
      fetchUserTestimonials(pagination.current, pagination.pageSize);
    }
  }, [activeTab]);

  const handleRecordingComplete = (videoData) => {
    setRecordedVideo(videoData);
    setCurrentStep(2);
    message.success("Recording completed successfully!");
  };

  const handleUploadComplete = (file) => {
    const videoData = {
      blob: file,
      url: URL.createObjectURL(file),
      duration: 0,
      fileSize: file.size,
    };
    setRecordedVideo(videoData);
    setCurrentStep(2);
    message.success("Video uploaded successfully!");
  };

  const uploadProps = {
    name: "video",
    multiple: false,
    accept: "video/*",
    beforeUpload: (file) => {
      const isVideo = file.type.startsWith("video/");
      if (!isVideo) {
        message.error("You can only upload video files!");
        return false;
      }

      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        message.error("Video must be smaller than 100MB!");
        return false;
      }

      handleUploadComplete(file);
      return false;
    },
  };

  const uploadVideoToServer = async () => {
    if (!recordedVideo) {
      console.error("❌ No recorded video found!");
      message.error("Please record or upload a video first!");
      return { success: false };
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const videoFile = new File(
        [recordedVideo.blob],
        `testimonial-${Date.now()}.webm`,
        { type: "video/webm" }
      );

      const uploadResult = await fileUploadService.uploadVideo(
        videoFile,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      if (uploadResult && uploadResult.fileUrl) {
        message.success("Video uploaded to server successfully!");
        const baseUrl = process.env.REACT_APP_API_URL;
        const fullVideoUrl = `${baseUrl}${uploadResult.fileUrl}`;
        return {
          success: true,
          videoUrl: fullVideoUrl,
          filename: uploadResult.filename,
          duration: recordedVideo.duration || 0,
          fileSize: recordedVideo.blob.size,
        };
      } else {
        throw new Error("Upload failed - no file URL received from server");
      }
    } catch (error) {
      console.error("Upload error details:", error);
      message.error("Failed to upload video: " + error.message);
      return { success: false };
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!recordedVideo) {
      message.error("Please record or upload a video first!");
      return;
    }

    setIsSubmitting(true);

    try {
      const uploadResult = await uploadVideoToServer();

      if (!uploadResult.success) {
        throw new Error("Video upload failed");
      }

      const testimonialData = {
        mobileNumber: authData.mobileNumber,
        videoUrl: uploadResult.videoUrl,
        videoDuration: uploadResult.duration,
        fileSize: uploadResult.fileSize,
        rating: values.rating,
        allowPublic:
          values.allowPublic !== undefined ? values.allowPublic : true,
        canContact: values.canContact || false,
      };

      const response = await testimonialService.createTestimonial(
        testimonialData
      );

      if (response.status) {
        message.success(
          "Testimonial submitted successfully! It will be reviewed soon."
        );
        setIsSubmitModalVisible(false);
        form.resetFields();

        setRecordedVideo(null);
        setCurrentStep(0);
        setUploadProgress(0);

        // Reload testimonials after submission (go to first page)
        fetchUserTestimonials(1, pagination.pageSize);
        setActiveTab("myTestimonials");
      } else {
        throw new Error(response.message || "Failed to submit testimonial");
      }
    } catch (error) {
      console.error("Submission error:", error);
      message.error("Submission failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueToSubmit = async () => {
    if (!recordedVideo) {
      message.error("Please record or upload a video first!");
      return;
    }
    setIsSubmitModalVisible(true);
  };

  // ========== ADDED: Handle pagination change ==========
  const handlePageChange = (page, pageSize) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize,
    });
    fetchUserTestimonials(page, pageSize);
  };

  // ========== UPDATED: Get current page testimonials ==========
  const getCurrentPageTestimonials = () => {
    return userTestimonials;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Title level={2}>Record Your Testimonial</Title>
      <Text type="secondary">
        Share your experience in a short video (max 3 minutes)
      </Text>

      {/* Tab Navigation */}
      <div className="my-6">
        <Space>
          <Button
            type={activeTab === "record" ? "primary" : "default"}
            onClick={() => setActiveTab("record")}
            size="large"
          >
            <VideoCameraOutlined /> Record New Testimonial
          </Button>
          <Button
            type={activeTab === "myTestimonials" ? "primary" : "default"}
            onClick={() => setActiveTab("myTestimonials")}
            size="large"
          >
            <UserOutlined /> My Testimonials
          </Button>
        </Space>
      </div>

      {activeTab === "record" && (
        <>
          {/* Steps */}
          <div className="my-8">
            <Steps current={currentStep} items={steps} />
          </div>

          {/* Step 1: Choose Method */}
          {currentStep === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                className="text-center cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setCurrentStep(1)}
              >
                <VideoCameraOutlined className="text-6xl text-blue-500 mb-4" />
                <Title level={3}>Record Now</Title>
                <Text type="secondary" className="block mb-4">
                  Record a new video using your camera
                </Text>
                <Button type="primary" size="large">
                  Start Recording
                </Button>
              </Card>
            </div>
          )}

          {/* Step 2: Record Video */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <VideoRecorder
                onRecordingComplete={handleRecordingComplete}
                maxDuration={180}
                showPreview={true}
              />

              <Button onClick={() => setCurrentStep(0)}>Back to Methods</Button>
            </div>
          )}

          {/* Step 3: Review & Submit */}
          {currentStep === 2 && recordedVideo && (
            <div className="space-y-6">
              <Card className="shadow-sm">
                <Title level={4}>Ready to Submit</Title>
                <Text type="secondary" className="block mb-4">
                  Your video is ready. Add details and submit for review.
                </Text>

                <div className="bg-gray-100 p-4 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <VideoCameraOutlined className="text-2xl text-blue-500" />
                    <div className="flex-1">
                      <Text strong>Video Recording</Text>
                      <br />
                      <Text type="secondary">
                        Size:{" "}
                        {(recordedVideo.fileSize / (1024 * 1024)).toFixed(2)} MB
                        {recordedVideo.duration > 0 &&
                          ` • Duration: ${formatDuration(
                            recordedVideo.duration
                          )}`}
                      </Text>
                    </div>
                    <Button onClick={() => setCurrentStep(1)}>Re-record</Button>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-6">
                  <Button onClick={() => setCurrentStep(0)}>
                    Back to Start
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleContinueToSubmit}
                    loading={isUploading}
                    icon={<CheckCircleOutlined />}
                    size="large"
                  >
                    {isUploading ? "Processing..." : "Continue to Submit"}
                  </Button>
                </div>

                {isUploading && (
                  <div className="mt-4">
                    <Progress percent={uploadProgress} status="active" />
                    <Text type="secondary" className="block text-center mt-2">
                      Processing your video... {uploadProgress}%
                    </Text>
                  </div>
                )}
              </Card>

              <Alert
                message="Before You Submit"
                description="Make sure your video is clear, well-lit, and conveys your message effectively. Testimonials with good audio and video quality have higher approval rates."
                type="info"
                showIcon
              />
            </div>
          )}
        </>
      )}

      {/* My Testimonials Tab */}
      {activeTab === "myTestimonials" && (
        <div className="space-y-6">
          <Card>
            <Title level={3}>My Testimonials ({pagination.total})</Title>
            <Text type="secondary">
              Manage your submitted testimonials and track their status
            </Text>

            <Spin spinning={testimonialsLoading}>
              <List
                dataSource={getCurrentPageTestimonials()}
                renderItem={(testimonial) => (
                  <List.Item>
                    <Card
                      style={{ width: "100%" }}
                      actions={[
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() =>
                            window.open(testimonial.videoUrl, "_blank")
                          }
                        >
                          Open In New Tab
                        </Button>,
                        <Popconfirm
                          title="Delete this testimonial?"
                          description="Are you sure you want to delete this testimonial? This action cannot be undone."
                          onConfirm={() =>
                            handleDeleteTestimonial(testimonial.id)
                          }
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button type="link" danger icon={<DeleteOutlined />}>
                            Delete
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* Video Preview */}
                        <div className="flex-1">
                          <div className="bg-black rounded-lg overflow-hidden">
                            <video
                              controls
                              className="w-full h-48 object-cover"
                            >
                              <source
                                src={testimonial.videoUrl}
                                type="video/webm"
                              />
                              <source
                                src={testimonial.videoUrl}
                                type="video/mp4"
                              />
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        </div>

                        {/* Testimonial Details */}
                        <div className="flex-1">
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <div className="flex justify-between items-start">
                              <Title level={4} className="mb-2">
                                {testimonial.title}
                              </Title>
                              <Tag color={getStatusColor(testimonial.status)}>
                                {testimonial.status.toUpperCase()}
                              </Tag>
                            </div>

                            <Text type="secondary">
                              {testimonial.description}
                            </Text>

                            <Space wrap>
                              {testimonial.rating && (
                                <Tag icon="⭐">
                                  Rating: {testimonial.rating}/5
                                </Tag>
                              )}
                              {testimonial.category && (
                                <Tag color="blue">{testimonial.category}</Tag>
                              )}
                              <Tag icon={<CalendarOutlined />}>
                                {formatDate(testimonial.createdAt)}
                              </Tag>
                              <Tag>Views: {testimonial.views || 0}</Tag>
                            </Space>

                            <div className="text-sm text-gray-500">
                              <Text>
                                Size:{" "}
                                {(testimonial.fileSize / (1024 * 1024)).toFixed(
                                  2
                                )}{" "}
                                MB
                              </Text>
                              {testimonial.reviewNotes && (
                                <>
                                  <br />
                                  <Text strong>Review Notes: </Text>
                                  <Text>{testimonial.reviewNotes}</Text>
                                </>
                              )}
                            </div>
                          </Space>
                        </div>
                      </div>
                    </Card>
                  </List.Item>
                )}
                locale={{
                  emptyText: (
                    <Empty
                      description="No testimonials found. Record your first testimonial!"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      <Button
                        type="primary"
                        onClick={() => setActiveTab("record")}
                        icon={<VideoCameraOutlined />}
                      >
                        Record Your First Testimonial
                      </Button>
                    </Empty>
                  ),
                }}
              />

              {/* ========== ADDED: Pagination Component ========== */}
              {pagination.total > 0 && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} testimonials`
                    }
                    pageSizeOptions={["5", "10", "20", "50"]}
                  />
                </div>
              )}
            </Spin>
          </Card>
        </div>
      )}

      {/* Submit Modal */}
      <Modal
        title="Submit Your Testimonial"
        open={isSubmitModalVisible}
        onCancel={() => setIsSubmitModalVisible(false)}
        footer={null}
        width={600}
        maskClosable={false}
        closable={!isSubmitting}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
          initialValues={{
            allowPublic: true,
            rating: 5,
            canContact: false,
          }}
        >
          <Form.Item
            name="rating"
            label="Overall Rating"
            rules={[{ required: true, message: "Please provide a rating!" }]}
          >
            <Select placeholder="How would you rate your experience?">
              <Option value={5}>
                ⭐⭐⭐⭐⭐ Excellent - Exceeded expectations
              </Option>
              <Option value={4}>
                ⭐⭐⭐⭐ Very Good - Met expectations well
              </Option>
              <Option value={3}>⭐⭐⭐ Good - Satisfactory experience</Option>
              <Option value={2}>⭐⭐ Fair - Some issues encountered</Option>
              <Option value={1}>⭐ Poor - Significant problems</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button
              onClick={() => setIsSubmitModalVisible(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckCircleOutlined />}
              size="large"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Testimonial"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default RecordTestimonial;
